import React, { useState } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import Input from './ui/Input';
import { useCreateCoupon } from '@/hooks/useApi';
import { getErrorMessage } from '@/lib/api';

interface CreateCouponModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateCouponModal: React.FC<CreateCouponModalProps> = ({ isOpen, onClose }) => {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('fixed');
  const [discountValue, setDiscountValue] = useState(1000);
  const [usageLimit, setUsageLimit] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const createCoupon = useCreateCoupon();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createCoupon.mutateAsync({
        code: code.toUpperCase(),
        description,
        discountType,
        discountValue,
        usageLimit: Number(usageLimit),
        expiresAt: expiresAt || undefined,
      });
      handleClose();
    } catch {
      // Handled below via createCoupon.isError / createCoupon.error; this
      // catch just stops the mutation rejection from becoming an unhandled
      // promise rejection.
    }
  };

  const handleClose = () => {
    setCode('');
    setDescription('');
    setDiscountType('fixed');
    setDiscountValue(1000);
    setUsageLimit('');
    setExpiresAt('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create Coupon" size="md">
      <form onSubmit={handleSubmit} className="space-y-5">
        {createCoupon.isError && (
          <div role="alert" className="rounded-xl border border-error/30 bg-error/10 p-3 text-sm text-foreground">
            {getErrorMessage(createCoupon.error)}
          </div>
        )}
        <Input
          label="Coupon Code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g., WELCOME50"
          required
        />

        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g., Welcome credit for new users"
          required
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-foreground">Coupon gives</label>
            <select
              value={discountType}
              onChange={(e) => {
                const t = e.target.value as 'percentage' | 'fixed';
                setDiscountType(t);
                setDiscountValue(t === 'percentage' ? 10 : 1000);
              }}
              className="block h-11 w-full rounded-xl border border-border bg-surface px-3.5 text-[15px] text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40"
            >
              <option value="fixed">Credits now</option>
              <option value="percentage">Bonus on next purchase (%)</option>
            </select>
          </div>
          <Input
            label={discountType === 'percentage' ? 'Extra credits (%)' : 'Credits'}
            type="number"
            value={discountValue.toString()}
            onChange={(e) => setDiscountValue(Number(e.target.value))}
            min={1}
            max={discountType === 'percentage' ? 100 : undefined}
            required
          />
        </div>

        <p className="-mt-2 text-xs text-muted-foreground">
          {discountType === 'percentage'
            ? `Each person who redeems it gets ${discountValue || 0}% extra credits on their next credit purchase.`
            : `Each person who redeems it gets ${(discountValue || 0).toLocaleString('en-NG')} credits (₦${((discountValue || 0) / 10).toLocaleString('en-NG')}) straight away.`}
          {' '}Each person can redeem a coupon once.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Usage limit"
            type="number"
            value={usageLimit}
            onChange={(e) => setUsageLimit(e.target.value)}
            placeholder="e.g., 100"
            min={1}
            max={1000000}
            required
          />
          <Input
            label="Expiry Date (optional)"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={createCoupon.isLoading}
            disabled={!code.trim() || !description.trim() || !(Number(usageLimit) >= 1)}
          >
            Create Coupon
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateCouponModal;
